import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Badge, Button, HStack, IconButton, Input, Spinner, Table, Text, VStack } from "@chakra-ui/react";
import { HiArrowRight, HiBarsArrowDown, HiBarsArrowUp } from "react-icons/hi2";
import InteractionService from "@/services/agency/InteractionService";
import { showToast } from "@/components/ui/toaster";
import { ChannelBadge, DetailTableShell, InteractionStatusBadge, tableHeaderStyle } from "@/components/ui/detail/primitives";

const FLOW_KIND = { Recruitment: "Rekrutacja", Campaign: "Kampania" };

const fmtDateTime = (d) => (d ? new Date(d).toLocaleString("pl-PL") : "—");

const COLUMNS = ["Kanał", "Z kim", "Scenariusz", "Rekrutacja / Kampania", "Status", "Utworzono", "Akcje"];

const PAGE_SIZE = 50;

const ClientInteractionsTab = ({ clientId, onCount }) => {
    const navigate = useNavigate();
    const [rows, setRows] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    const [pageSize, setPageSize] = useState(PAGE_SIZE);

    const [search, setSearch] = useState("");
    const [sortDirection, setSortDirection] = useState("desc");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const debounceRef = useRef(null);

    useEffect(() => {
        clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => setDebouncedSearch(search.trim()), 350);
        return () => clearTimeout(debounceRef.current);
    }, [search]);

    useEffect(() => {
        setIsLoading(true);
        InteractionService.getByClient(clientId, {
            pageSize,
            search: debouncedSearch || null,
            sortDirection,
        })
            .then((data) => {
                const items = Array.isArray(data?.items) ? data.items : (Array.isArray(data) ? data : []);
                setRows(items);
                const total = Number.isFinite(data?.totalCount) ? data.totalCount : items.length;
                setTotalCount(total);
                onCount?.(total);
            })
            .catch(() => showToast("Błąd", "Nie udało się pobrać interakcji", "error"))
            .finally(() => setIsLoading(false));
    }, [clientId, debouncedSearch, sortDirection, pageSize, onCount]);

    return (
        <VStack w="full" align="flex-start" gap={4} pt={4}>
            <HStack gap={2} w="full" maxW="560px">
                <Input
                    variant="primary"
                    size="sm"
                    placeholder="Szukaj po kolejce / numerze / e-mailu…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                />
                <IconButton
                    size="sm"
                    variant="outlined"
                    aria-label="Zmień kierunek sortowania po dacie"
                    onClick={() => setSortDirection((d) => (d === "desc" ? "asc" : "desc"))}
                    title={sortDirection === "desc" ? "Najnowsze najpierw" : "Najstarsze najpierw"}
                >
                    {sortDirection === "desc" ? <HiBarsArrowDown /> : <HiBarsArrowUp />}
                </IconButton>
            </HStack>

            {isLoading ? (
                <VStack w="full" py={8} align="center"><Spinner color="white" size="md" /></VStack>
            ) : (
                <>
                    <Text color="text.grey" fontSize="xs">{rows.length} z {totalCount} interakcji</Text>
                    <DetailTableShell>
                        <Table.Root>
                            <Table.Header>
                                <Table.Row bg="bg.card" borderBottom="1px solid" borderColor="border.primary">
                                    {COLUMNS.map((col, i) => (
                                        <Table.ColumnHeader key={col} {...tableHeaderStyle} textAlign={i === COLUMNS.length - 1 ? "right" : "left"}>
                                            {col}
                                        </Table.ColumnHeader>
                                    ))}
                                </Table.Row>
                            </Table.Header>
                            <Table.Body>
                                {rows.length === 0 ? (
                                    <Table.Row bg="bg.secondary">
                                        <Table.Cell colSpan={COLUMNS.length} py={8} border="none" textAlign="center">
                                            <Text color="text.grey" fontSize="sm">Brak interakcji</Text>
                                        </Table.Cell>
                                    </Table.Row>
                                ) : rows.map((r) => (
                                    <Table.Row key={r.id} bg="bg.secondary" borderBottom="1px solid" borderColor="border.primary" _hover={{ bg: "bg.highlight" }} transition="background 0.15s">
                                        <Table.Cell py={4} px={5} border="none">
                                            <ChannelBadge channel={r.channel} />
                                        </Table.Cell>
                                        <Table.Cell py={4} px={5} border="none">
                                            <Text color="white" fontSize="sm" truncate maxW="240px" title={r.participant ?? ""}>
                                                {r.participant || "—"}
                                            </Text>
                                        </Table.Cell>
                                        <Table.Cell py={4} px={5} border="none">
                                            <Text color="text.grey" fontSize="sm">{r.scenario?.name ?? "—"}</Text>
                                        </Table.Cell>
                                        <Table.Cell py={4} px={5} border="none">
                                            <HStack gap={2}>
                                                <Text color="text.grey" fontSize="sm">{r.flowName ?? "—"}</Text>
                                                {FLOW_KIND[r.flowKind] && (
                                                    <Badge size="sm" colorPalette={r.flowKind === "Campaign" ? "purple" : "blue"} variant="surface">
                                                        {FLOW_KIND[r.flowKind]}
                                                    </Badge>
                                                )}
                                            </HStack>
                                        </Table.Cell>
                                        <Table.Cell py={4} px={5} border="none">
                                            <InteractionStatusBadge status={r.status} />
                                        </Table.Cell>
                                        <Table.Cell py={4} px={5} border="none">
                                            <Text color="text.grey" fontSize="sm">{fmtDateTime(r.createdAtUtc)}</Text>
                                        </Table.Cell>
                                        <Table.Cell py={4} px={5} border="none" textAlign="right">
                                            <IconButton variant="outlined" size="sm" aria-label="Szczegóły interakcji" onClick={() => navigate(`/clients/${clientId}/interactions/${r.id}`)}>
                                                <HiArrowRight />
                                            </IconButton>
                                        </Table.Cell>
                                    </Table.Row>
                                ))}
                            </Table.Body>
                        </Table.Root>
                    </DetailTableShell>
                    {rows.length < totalCount && (
                        <Button variant="outline" size="sm" alignSelf="center" onClick={() => setPageSize((n) => n + PAGE_SIZE)}>
                            Załaduj więcej
                        </Button>
                    )}
                </>
            )}
        </VStack>
    );
};

export default ClientInteractionsTab;
